/**
 * AdminTeam — /admin/team.
 *
 * Contact-card grid of the OJB roster, sourced primarily from the
 * Microsoft Entra "OJB Company Wide" group via the Graph sync.
 * ojb.com scraper is kept as a fallback / legacy source while
 * Graph scope consent is rolling out.
 *
 * UX:
 *   - Search bar filters name / email / title / office.
 *   - Top-action bar: Sync from Graph · Run scraper · Add member ·
 *     More menu (archive list, delete stale, etc.)
 *   - Grid of cards (3 columns on desktop). Each card shows the
 *     member's photo (or initials avatar), name, title, email,
 *     credentials + office, and a footer with per-person meeting
 *     stats matched from the users table by email.
 *   - Per-card overflow menu: archive / unarchive / edit / delete.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  GitMerge,
  Mail,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { api, ApiError } from '../../lib/apiClient.ts';
import { NO_AUTOFILL } from '../../lib/noAutofill.ts';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '../../components/ui/Modal.tsx';

interface MemberStats {
  monthToDate: number;
  finalizedAllTime: number;
}

interface TeamMember {
  id: string;
  fullName: string;
  email?: string | null;
  /** Sync-sourced office (Graph or scraper). May be null. The card
   *  prefers `officeOverride` when set. */
  office: string | null;
  /** Admin-set override that wins over `office`. Setting null on
   *  PATCH clears the override and the Graph value re-takes effect. */
  officeOverride?: string | null;
  title: string | null;
  headshotUrl?: string | null;
  isArchived?: boolean;
  source?: 'scraper' | 'manual' | 'graph';
  stats?: MemberStats;
  /** ISO timestamp of first sighting in ojb_team. Admin-only field;
   *  powers the "Recently added" sort option. */
  firstSeenAt?: string;
}

/** Canonical OJB office options used by the inline-edit dropdown.
 *  Even if the current roster has no one in a given office (e.g.
 *  Denver after staff churn), the option remains pickable so admins
 *  can re-assign without IT updating Entra first. */
const OJB_OFFICES = [
  'Boston',
  'Dallas',
  'Denver',
  'Houston',
  'Philadelphia',
  'San Diego',
] as const;

/** Effective-office helper — single source of truth for "what office
 *  does this card render?" Used by both the card body and the office
 *  filter chip predicate so both stay in sync. */
function effectiveOffice(m: TeamMember): string | null {
  return m.officeOverride ?? m.office ?? null;
}

type SortBy = 'name' | 'mtd' | 'finalized' | 'recent';

interface GraphSyncResult {
  groupName: string;
  groupId: string;
  scanned: number;
  inserted: number;
  updated: number;
  archived: number;
  photosFetched: number;
  photosSkipped: number;
  skippedNonPerson?: number;
  warnings: string[];
}

interface ScrapeResult {
  ok: boolean;
  dryRun: boolean;
  scrapedCount: number;
  inserted: number;
  updated: number;
  warnings: string[];
}

export function AdminTeam() {
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [filter, setFilter] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>('name');
  const [drawerMember, setDrawerMember] = useState<TeamMember | null>(null);
  // Active office filter — set by clicking an office chip at the top.
  // null = show all offices. Clicking the same chip again clears it
  // (single-select with self-toggle).
  const [officeFilter, setOfficeFilter] = useState<string | null>(null);

  const roster = useQuery({
    queryKey: ['ojb-team', 'admin', showArchived],
    queryFn: () =>
      api.get<{ members: TeamMember[] }>('/ojb-team', {
        limit: 500,
        includeArchived: showArchived ? 1 : 0,
      }),
  });

  const runGraphSync = useMutation({
    mutationFn: () =>
      api.post<GraphSyncResult>('/ojb-team/run-graph-sync'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ojb-team'] });
    },
  });

  const runScraper = useMutation({
    mutationFn: (opts: { approveSafetyOverride?: boolean } = {}) =>
      api.post<ScrapeResult>('/ojb-team/run-scraper', opts),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ojb-team'] });
    },
  });

  const deleteStale = useMutation({
    mutationFn: (opts: { minutesAgo?: number } = {}) =>
      api.post<{ deleted: number; minutesAgo: number }>(
        '/ojb-team/delete-stale',
        opts,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ojb-team'] });
    },
  });

  const purgeArchived = useMutation({
    mutationFn: () =>
      api.post<{
        deleted: number;
        attendeesUnlinked: number;
        usersUnlinked: number;
      }>('/ojb-team/purge-archived'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ojb-team'] });
    },
  });

  const mergeDuplicates = useMutation({
    mutationFn: () =>
      api.post<{
        leftoversDeleted: number;
        groupsMerged: number;
        rowsDeleted: number;
        conflictsSkipped: number;
        details: Array<{
          key: string;
          winnerId: string;
          winnerSource: 'scraper' | 'manual' | 'graph';
          loserIds: string[];
          loserSources: Array<'scraper' | 'manual' | 'graph'>;
          photoSource: 'website' | 'graph' | 'none';
          fullName: string;
        }>;
      }>('/ojb-team/merge-duplicates'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ojb-team'] });
    },
  });

  const patchMember = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Partial<TeamMember>;
    }) =>
      api.patch<{ member: TeamMember }>(`/ojb-team/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ojb-team'] });
    },
  });

  const members = roster.data?.members ?? [];

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let list = q
      ? members.filter((m) => {
          const hay = [m.fullName, m.email ?? '', m.title ?? '', m.office ?? '']
            .join(' ')
            .toLowerCase();
          return hay.includes(q);
        })
      : members;
    if (officeFilter) {
      list = list.filter((m) => {
        const eff = effectiveOffice(m);
        return officeFilter === 'Unassigned' ? !eff : eff === officeFilter;
      });
    }
    // Apply sort. Numeric tie-breakers fall through to alphabetical
    // on fullName so cards in the same bucket stay scannable.
    const sorted = [...list];
    sorted.sort((a, b) => {
      if (sortBy === 'mtd') {
        const d = (b.stats?.monthToDate ?? 0) - (a.stats?.monthToDate ?? 0);
        if (d !== 0) return d;
      } else if (sortBy === 'finalized') {
        const d =
          (b.stats?.finalizedAllTime ?? 0) - (a.stats?.finalizedAllTime ?? 0);
        if (d !== 0) return d;
      } else if (sortBy === 'recent') {
        const at = a.firstSeenAt ? new Date(a.firstSeenAt).getTime() : 0;
        const bt = b.firstSeenAt ? new Date(b.firstSeenAt).getTime() : 0;
        if (bt !== at) return bt - at;
      }
      return a.fullName.localeCompare(b.fullName);
    });
    return sorted;
  }, [members, filter, sortBy, officeFilter]);

  // Office breakdown chips — computed once per members change. Kept
  // client-side so it reacts to archive toggles without a refetch.
  // Uses the effective office (override-aware) so chips reflect what
  // the cards actually display.
  const officeBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of members) {
      const key = effectiveOffice(m) || 'Unassigned';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([office, count]) => ({ office, count }))
      .sort((a, b) => b.count - a.count);
  }, [members]);

  const graphError =
    runGraphSync.error instanceof ApiError
      ? {
          message: runGraphSync.error.message,
          scopeMissing:
            (runGraphSync.error.data as { scopeMissing?: boolean })
              ?.scopeMissing ?? false,
        }
      : null;

  return (
    <div>
      {/* Office breakdown chips — click an office to filter the grid
          to just that location. Click the active chip again (or the
          "N staff" pill) to clear. Single-select. */}
      {officeBreakdown.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px]">
          <button
            type="button"
            onClick={() => setOfficeFilter(null)}
            disabled={officeFilter === null}
            title={
              officeFilter === null
                ? 'Showing all offices'
                : `Clear office filter (${officeFilter})`
            }
            className={
              'rounded-full border px-2.5 py-1 font-semibold transition-colors ' +
              (officeFilter === null
                ? 'cursor-default border-pine/30 bg-pine/10 text-pine'
                : 'border-charcoal/20 bg-cloud/60 text-charcoal/70 hover:border-pine hover:text-pine')
            }
          >
            {members.length} staff
            {officeFilter !== null && (
              <span className="ml-1 text-[10px] text-charcoal/50">
                (clear)
              </span>
            )}
          </button>
          {officeBreakdown.map(({ office, count }) => {
            const isActive = officeFilter === office;
            return (
              <button
                key={office}
                type="button"
                onClick={() =>
                  setOfficeFilter(isActive ? null : office)
                }
                title={
                  isActive ? `Clear ${office} filter` : `Show only ${office}`
                }
                // Office breakdown chip — same .filter-chip family
                // as AdminUsers / AdminMeetings.
                className={
                  'rounded-full border px-3 py-1 text-[12px] font-medium transition-colors ' +
                  (isActive
                    ? 'border-pine bg-pine text-cream'
                    : 'border-charcoal/20 bg-cloud/60 text-charcoal/70 hover:border-pine hover:text-pine')
                }
              >
                <b className={'tabular-nums ' + (isActive ? '' : 'text-charcoal')}>
                  {count}
                </b>{' '}
                <span className={isActive ? '' : 'text-charcoal/55'}>
                  {office}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Mockup .search-pill — rounded-[14px] cloud/60 + charcoal/15
          + py-3 pl-11 + pine focus ring. */}
      <div className="relative mb-3.5">
        <Search
          size={16}
          aria-hidden
          className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/45"
        />
        <input
          {...NO_AUTOFILL}
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search by name, email, role, or office…"
          className="w-full rounded-[14px] border border-charcoal/15 bg-cloud/60 py-3 pl-11 pr-4 text-[13px] text-charcoal placeholder:text-charcoal/45 focus:border-pine focus:bg-bone focus:outline-none focus:ring-[3px] focus:ring-pine/15"
        />
      </div>

      {/* Action bar */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Mockup .admin-btn (primary) — pine fill cream text. */}
          <button
            type="button"
            onClick={() => runGraphSync.mutate()}
            disabled={runGraphSync.isPending}
            className="inline-flex items-center gap-1.5 rounded-full border border-pine bg-pine px-4 py-2 text-[13px] font-medium text-cream transition-all hover:-translate-y-px hover:bg-pine-deep disabled:opacity-50"
            title="Pull the OJB Company Wide group from Entra (requires IT-granted scopes)."
          >
            {runGraphSync.isPending ? (
              'Syncing…'
            ) : (
              <>
                <RefreshCw size={14} />
                Sync from Graph
              </>
            )}
          </button>
          {/* Mockup .admin-btn--outline — transparent + charcoal/20
              border, hover pine border + pine-soft bg. */}
          <button
            type="button"
            onClick={() => runScraper.mutate({})}
            disabled={runScraper.isPending}
            className="rounded-full border border-charcoal/20 bg-transparent px-4 py-2 text-[13px] font-medium text-charcoal transition-colors hover:border-pine hover:bg-pine/[0.08] hover:text-pine disabled:opacity-50"
            title="Run the legacy ojb.com/team scraper (fallback while Graph consent is pending)."
          >
            {runScraper.isPending ? 'Scraping…' : 'ojb.com scrape'}
          </button>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="rounded-full border border-charcoal/20 bg-transparent px-4 py-2 text-[13px] font-medium text-charcoal transition-colors hover:border-pine hover:bg-pine/[0.08] hover:text-pine"
          >
            + Add member
          </button>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-[11px]">
            <span className="font-semibold uppercase tracking-[0.14em] text-charcoal/60">
              Sort
            </span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
              className="rounded-[8px] border border-charcoal/20 bg-bone px-2.5 py-1.5 text-[12px] text-charcoal focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
            >
              <option value="name">Name A–Z</option>
              <option value="mtd">Most active this month</option>
              <option value="finalized">Most finalized all-time</option>
              <option value="recent">Recently added</option>
            </select>
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-charcoal/70">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 accent-pine"
            />
            Show archived
          </label>
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className="rounded-full border border-charcoal/20 bg-transparent p-2 text-charcoal transition-colors hover:border-pine hover:bg-pine/[0.08] hover:text-pine"
            title="More maintenance actions"
            aria-label="More"
          >
            <MoreHorizontal size={16} />
          </button>
        </div>
      </div>

      {/* Graph sync result / error banners */}
      {runGraphSync.isSuccess && runGraphSync.data && (
        <div className="mb-3.5 rounded-2xl border border-pine/25 bg-pine/[0.06] px-4 py-3 text-[13px] text-pine">
          ✓ Synced <b>{runGraphSync.data.scanned}</b> members from{' '}
          <b>{runGraphSync.data.groupName}</b>.{' '}
          {runGraphSync.data.inserted} new,{' '}
          {runGraphSync.data.updated} updated,{' '}
          {runGraphSync.data.archived} archived,{' '}
          {runGraphSync.data.photosFetched} photos fetched (
          {runGraphSync.data.photosSkipped} skipped).
          {runGraphSync.data.skippedNonPerson !== undefined &&
            runGraphSync.data.skippedNonPerson > 0 && (
              <>
                {' '}
                <span className="text-charcoal/70">
                  Filtered out{' '}
                  <b className="text-pine">
                    {runGraphSync.data.skippedNonPerson}
                  </b>{' '}
                  shared mailbox{' '}
                  {runGraphSync.data.skippedNonPerson === 1
                    ? 'entry'
                    : 'entries'}{' '}
                  (conference rooms, etc.).
                </span>
              </>
            )}
        </div>
      )}
      {graphError && (
        <div
          className={
            'mb-3.5 rounded-2xl border px-4 py-3 text-[13px] ' +
            (graphError.scopeMissing
              ? 'border-amber-300/60 bg-amber-50 text-amber-900'
              : 'border-terra-cotta/30 bg-terra-cotta/10 text-terra-cotta')
          }
        >
          {graphError.scopeMissing && <b>Graph scope pending. </b>}
          {graphError.message}
        </div>
      )}
      {purgeArchived.isSuccess && purgeArchived.data && (
        <div className="mb-4 rounded-2xl border border-pine/30 bg-pine/5 px-4 py-3 text-sm text-pine">
          ✓ Purged <b>{purgeArchived.data.deleted}</b> archived row
          {purgeArchived.data.deleted === 1 ? '' : 's'}.{' '}
          <span className="text-charcoal/70">
            Unlinked {purgeArchived.data.attendeesUnlinked} attendee ref
            {purgeArchived.data.attendeesUnlinked === 1 ? '' : 's'} and{' '}
            {purgeArchived.data.usersUnlinked} user profile link
            {purgeArchived.data.usersUnlinked === 1 ? '' : 's'} first.
          </span>
        </div>
      )}
      {mergeDuplicates.isSuccess && mergeDuplicates.data && (
        <div className="mb-4 rounded-2xl border border-pine/30 bg-pine/5 px-4 py-3 text-sm text-pine">
          {(() => {
            const d = mergeDuplicates.data!;
            const total = d.rowsDeleted + d.leftoversDeleted;
            if (total === 0) {
              return <>✓ No duplicates found - roster is clean.</>;
            }
            const bits: string[] = [];
            if (d.groupsMerged > 0) {
              bits.push(
                `merged ${d.groupsMerged} name group${d.groupsMerged === 1 ? '' : 's'} (${d.rowsDeleted} row${d.rowsDeleted === 1 ? '' : 's'} deleted)`,
              );
            }
            if (d.leftoversDeleted > 0) {
              bits.push(
                `cleaned up ${d.leftoversDeleted} leftover soft-merged row${d.leftoversDeleted === 1 ? '' : 's'}`,
              );
            }
            return (
              <>
                ✓ {bits.join(', ')}. Website (ojb.com) photos preferred
                over Entra.
                {d.conflictsSkipped > 0 && (
                  <>
                    {' '}
                    <span className="text-amber-700">
                      {d.conflictsSkipped} nickname-matched group
                      {d.conflictsSkipped === 1 ? '' : 's'} skipped
                      (conflicting email / Entra id - likely different
                      people).
                    </span>
                  </>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Count line */}
      <div className="mb-3 text-[11px] text-charcoal/55">
        {filtered.length} of {members.length} team member
        {members.length === 1 ? '' : 's'}
        {showArchived ? ' (including archived)' : ''}
      </div>

      {/* Loading / error / empty */}
      {roster.isLoading && (
        <div className="py-10 text-center text-sm italic text-charcoal/55">
          Loading roster…
        </div>
      )}
      {roster.isError && (
        <div className="rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 px-4 py-3 text-[13px] text-terra-cotta">
          Failed to load team roster.
        </div>
      )}
      {!roster.isLoading && filtered.length === 0 && (
        <div className="rounded-[14px] border border-dashed border-charcoal/20 px-4 py-10 text-center text-[12px] italic text-charcoal/55">
          {members.length === 0
            ? 'No team members yet. Click "Sync from Graph" to pull from Entra, or "ojb.com scrape" as a fallback.'
            : `No members match "${filter}".`}
        </div>
      )}

      {/* Contact-card grid */}
      {filtered.length > 0 && (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((m) => (
            <ContactCard
              key={m.id}
              member={m}
              onPatch={(patch) =>
                patchMember.mutate({ id: m.id, body: patch })
              }
              onOpen={() => setDrawerMember(m)}
            />
          ))}
        </ul>
      )}

      {/* Member detail drawer — right-slide, recent meetings + 30d
          activity sparkline. Opens on card click. */}
      {drawerMember && (
        <TeamMemberDrawer
          member={drawerMember}
          onClose={() => setDrawerMember(null)}
        />
      )}

      {/* Add member modal */}
      <AddMemberModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={() => {
          setAddOpen(false);
          qc.invalidateQueries({ queryKey: ['ojb-team'] });
        }}
      />

      {/* Maintenance drawer / modal */}
      <Modal
        isOpen={moreOpen}
        onClose={() => setMoreOpen(false)}
        size="md"
        ariaLabel="Team maintenance actions"
      >
        <ModalHeader
          title="Team maintenance"
          subtitle="Less-common roster actions. Most of the time, use Sync from Graph above."
          onClose={() => setMoreOpen(false)}
        />
        <ModalBody>
          <div className="space-y-3 text-sm">
            <div>
              <button
                type="button"
                onClick={() => {
                  if (
                    confirm(
                      'Merge & deduplicate roster? Nickname-aware (Andy = Andrew), always keeps ojb.com website photos, HARD-DELETES loser rows, and cleans up any leftovers from prior soft-merges. Attendee references are reassigned first. Safe to re-run.',
                    )
                  ) {
                    mergeDuplicates.mutate();
                    setMoreOpen(false);
                  }
                }}
                disabled={mergeDuplicates.isPending}
                className="w-full rounded-xl border border-pine/30 bg-pine/5 px-3 py-2 text-left text-pine transition-colors hover:border-pine hover:bg-pine/10 disabled:opacity-50"
              >
                <div className="inline-flex items-center gap-1.5 font-semibold">
                  {mergeDuplicates.isPending ? (
                    'Merging…'
                  ) : (
                    <>
                      <GitMerge size={14} />
                      Merge &amp; deduplicate
                    </>
                  )}
                </div>
                <div className="mt-0.5 text-[11px] text-pine/70">
                  Nickname-aware (Andy = Andrew), unions Graph + website
                  data, always keeps ojb.com photos, hard-deletes losers.
                  Skips groups where two rows have different emails or
                  Entra ids (likely different people).
                </div>
              </button>
            </div>
            <div>
              <button
                type="button"
                onClick={() => {
                  if (
                    confirm(
                      'Hard-delete EVERY archived team row? Attendee refs + user profile links are nulled first so meeting attribution survives. This cannot be undone.',
                    )
                  ) {
                    purgeArchived.mutate();
                    setMoreOpen(false);
                  }
                }}
                disabled={purgeArchived.isPending}
                className="w-full rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-left text-red-700 transition-colors hover:border-red-500 hover:bg-red-100 disabled:opacity-50"
              >
                <div className="inline-flex items-center gap-1.5 font-semibold">
                  {purgeArchived.isPending ? (
                    'Purging…'
                  ) : (
                    <>
                      <Trash2 size={14} />
                      Purge archived members
                    </>
                  )}
                </div>
                <div className="mt-0.5 text-[11px] text-red-700/80">
                  Hard-deletes every is_archived=true row (shared
                  mailboxes auto-archived by the heuristic, dropped
                  Graph members, manually-archived staff, etc).
                  Attendee FK refs are nulled first so past meeting
                  attribution still resolves.
                </div>
              </button>
            </div>
            <div>
              <button
                type="button"
                onClick={() => {
                  if (
                    confirm(
                      'Delete ALL scraper-origin roster rows? Graph-sourced + manual rows are untouched.',
                    )
                  ) {
                    deleteStale.mutate({ minutesAgo: 0 });
                    setMoreOpen(false);
                  }
                }}
                disabled={deleteStale.isPending}
                className="w-full rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-left text-red-700 transition-colors hover:border-red-500 hover:bg-red-100 disabled:opacity-50"
              >
                <div className="inline-flex items-center gap-1.5 font-semibold">
                  <Trash2 size={14} />
                  Purge scraper rows
                </div>
                <div className="mt-0.5 text-[11px] text-red-700/80">
                  Hard-deletes rows with source='scraper'. Use after a
                  clean Graph sync to remove legacy entries.
                </div>
              </button>
            </div>
            <div>
              <button
                type="button"
                onClick={() => {
                  deleteStale.mutate({ minutesAgo: 10 });
                  setMoreOpen(false);
                }}
                disabled={deleteStale.isPending}
                className="w-full rounded-xl border border-charcoal/20 bg-white px-3 py-2 text-left transition-colors hover:border-pine disabled:opacity-50"
              >
                <div className="font-semibold text-charcoal">
                  Remove stale scraper rows
                </div>
                <div className="mt-0.5 text-[11px] text-charcoal/60">
                  Deletes scraper rows not seen in the last 10 minutes
                  (use right after a fresh ojb.com scrape).
                </div>
              </button>
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="flex items-center justify-end">
          <button
            type="button"
            onClick={() => setMoreOpen(false)}
            className="rounded-full border border-charcoal/25 px-4 py-2 text-[13px] font-medium text-charcoal transition-colors hover:border-pine hover:text-pine"
          >
            Close
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

/* ─── Card ───────────────────────────────────────────────────────── */

function ContactCard({
  member,
  onPatch,
  onOpen,
}: {
  member: TeamMember;
  onPatch: (patch: Partial<TeamMember>) => void;
  /** Fired when the user clicks anywhere on the card except an
   *  interactive child (overflow menu, email link). Opens the
   *  side drawer with meetings + activity. */
  onOpen: () => void;
}) {
  const { displayName, credentials } = splitName(member.fullName);
  const initials = initialsFor(displayName);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <li
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className={
        'group relative cursor-pointer overflow-hidden rounded-2xl border bg-cloud/60 p-4 shadow-sm transition-all hover:border-pine/30 hover:bg-cloud/80 hover:shadow-md ' +
        (member.isArchived
          ? 'border-charcoal/15 opacity-60'
          : 'border-charcoal/15')
      }
    >
      <div className="flex items-start gap-3">
        {/* Photo or initials */}
        {member.headshotUrl ? (
          <img
            src={member.headshotUrl}
            alt={displayName}
            className="h-14 w-14 shrink-0 rounded-full object-cover ring-1 ring-pine/20 transition-shadow group-hover:ring-pine/40"
            loading="lazy"
          />
        ) : (
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-pine/15 text-sm font-semibold text-pine ring-1 ring-pine/25 transition-shadow group-hover:ring-pine/40">
            {initials}
          </div>
        )}

        {/* Text block */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-charcoal">
                {displayName}
              </div>
              {member.title && (
                <div className="mt-0.5 flex items-center gap-1.5 truncate text-[12px] text-charcoal/70">
                  <Briefcase size={11} className="shrink-0 text-pine/70" />
                  <span className="truncate">{member.title}</span>
                </div>
              )}
              {member.email && (
                <a
                  href={`mailto:${member.email}`}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-0.5 flex items-center gap-1.5 truncate text-[12px] text-pine/90 hover:text-pine hover:underline"
                >
                  <Mail size={11} className="shrink-0 text-pine/70" />
                  <span className="truncate">{member.email}</span>
                </a>
              )}
            </div>

            {/* Overflow menu — stop card-click from bubbling here so
                Archive/Unarchive doesn't also pop the drawer. */}
            <div
              className="relative shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen((v) => !v);
                }}
                className="rounded-full p-1 text-charcoal/40 opacity-0 transition-colors hover:bg-cloud/60 hover:text-charcoal group-hover:opacity-100"
                aria-label="Member actions"
              >
                <MoreHorizontal size={14} />
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 top-full z-10 mt-1 w-44 overflow-hidden rounded-xl border border-charcoal/15 bg-white text-left text-[12px] shadow-lg"
                  onMouseLeave={() => setMenuOpen(false)}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPatch({ isArchived: !member.isArchived });
                      setMenuOpen(false);
                    }}
                    className="block w-full px-3 py-2 text-left hover:bg-cloud/40"
                  >
                    {member.isArchived ? 'Unarchive' : 'Archive'}
                  </button>
                  {member.source && (
                    <div className="border-t border-charcoal/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] text-charcoal/50">
                      source: {member.source}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mt-1.5 flex items-center gap-1.5 truncate text-[11px]">
            {credentials && (
              <span className="truncate font-medium uppercase tracking-[0.08em] text-pine/70">
                {credentials}
              </span>
            )}
            {credentials && (
              <span className="text-charcoal/30">·</span>
            )}
            <OfficeInlineEdit
              member={member}
              onPatch={onPatch}
            />
          </div>
        </div>
      </div>

      {/* Footer stats */}
      {member.stats && (
        <div className="mt-3 flex items-center justify-between border-t border-charcoal/10 pt-2.5 text-[11px] text-charcoal/55">
          <span className="flex items-baseline gap-1">
            <b className="text-[13px] font-semibold text-pine">
              {member.stats.monthToDate}
            </b>
            <span>this month</span>
          </span>
          <span className="flex items-baseline gap-1">
            <b className="text-[13px] font-semibold text-pine">
              {member.stats.finalizedAllTime}
            </b>
            <span>finalized</span>
          </span>
        </div>
      )}

      {member.isArchived && (
        <div className="mt-2 inline-block rounded-full bg-charcoal/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-charcoal/70">
          Archived
        </div>
      )}
    </li>
  );
}

/* ─── Office inline-edit ────────────────────────────────────────────
 * Click the office text on a card → dropdown of OJB offices opens.
 * Selecting an option PATCHes officeOverride. "(use Graph value)"
 * clears the override so the Graph-sourced office re-takes effect.
 *
 * A small ✎ pencil appears on hover over the office text so the
 * affordance is discoverable without making the column too busy. */

function OfficeInlineEdit({
  member,
  onPatch,
}: {
  member: TeamMember;
  onPatch: (patch: Partial<TeamMember>) => void;
}) {
  const [editing, setEditing] = useState(false);
  const eff = effectiveOffice(member);
  const isOverridden =
    member.officeOverride !== null &&
    member.officeOverride !== undefined &&
    member.officeOverride !== member.office;

  if (editing) {
    return (
      <select
        autoFocus
        value={eff ?? ''}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const next = e.target.value;
          onPatch({ officeOverride: next || null });
          setEditing(false);
        }}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setEditing(false);
          e.stopPropagation();
        }}
        className="rounded-[8px] border border-charcoal/15 bg-cream px-2 py-0.5 text-[11px] text-charcoal focus:border-pine focus:outline-none focus:ring-[2px] focus:ring-pine/15"
      >
        <option value="">(use Graph value)</option>
        {OJB_OFFICES.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      title={
        isOverridden
          ? `Override (Graph said "${member.office ?? 'nothing'}"). Click to change.`
          : 'Click to override'
      }
      className={
        'group/office inline-flex items-center gap-1 truncate rounded px-1 -mx-1 transition-colors hover:bg-pine/5 hover:text-pine ' +
        (eff
          ? isOverridden
            ? 'text-pine/70'
            : 'text-charcoal/55'
          : 'text-charcoal/40 italic')
      }
    >
      <span className="truncate">
        {eff ?? 'no office'}
      </span>
      {isOverridden && (
        <span className="rounded-full bg-pine/15 px-1 py-0 text-[8px] font-semibold uppercase tracking-wider text-pine">
          override
        </span>
      )}
      <Pencil
        size={9}
        className="text-charcoal/30 opacity-0 transition-opacity group-hover/office:opacity-100"
      />
    </button>
  );
}

/* ─── Add-member modal ───────────────────────────────────────────── */

function AddMemberModal({
  isOpen,
  onClose,
  onAdded,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [fullName, setFullName] = useState('');
  const [title, setTitle] = useState('');
  const [office, setOffice] = useState('');
  const add = useMutation({
    mutationFn: () =>
      api.post<{ member: TeamMember; reused: boolean }>('/ojb-team', {
        fullName: fullName.trim(),
        title: title.trim() || undefined,
        office: office.trim() || undefined,
      }),
    onSuccess: () => {
      setFullName('');
      setTitle('');
      setOffice('');
      onAdded();
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      ariaLabel="Add team member"
    >
      <ModalHeader
        title="Add team member"
        subtitle="For off-site staff or consultants the Graph sync won't pick up. Full name is required; other fields are optional."
        onClose={onClose}
      />
      <ModalBody>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (fullName.trim()) add.mutate();
          }}
        >
          <Labeled label="Full name (required)">
            <input
              {...NO_AUTOFILL}
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Sara Chen, FASLA"
              className="w-full rounded-lg border border-charcoal/20 bg-paper-light px-3 py-2 text-sm text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
            />
          </Labeled>
          <Labeled label="Title">
            <input
              {...NO_AUTOFILL}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Senior Associate"
              className="w-full rounded-lg border border-charcoal/20 bg-paper-light px-3 py-2 text-sm text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
            />
          </Labeled>
          <Labeled label="Office">
            <input
              {...NO_AUTOFILL}
              type="text"
              value={office}
              onChange={(e) => setOffice(e.target.value)}
              placeholder="Houston"
              className="w-full rounded-lg border border-charcoal/20 bg-paper-light px-3 py-2 text-sm text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
            />
          </Labeled>
          {add.isError && (
            <div className="rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 px-3 py-2 text-[12px] text-terra-cotta">
              {add.error instanceof Error
                ? add.error.message
                : 'Add failed'}
            </div>
          )}
        </form>
      </ModalBody>
      <ModalFooter className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={add.isPending}
          className="rounded-full border border-charcoal/25 px-4 py-2 text-[13px] font-medium text-charcoal transition-colors hover:border-pine hover:text-pine disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => fullName.trim() && add.mutate()}
          disabled={!fullName.trim() || add.isPending}
          className="rounded-full bg-pine px-4 py-2 text-[13px] font-medium text-cream transition-colors hover:bg-pine-deep disabled:opacity-50"
        >
          {add.isPending ? 'Adding…' : 'Add member'}
        </button>
      </ModalFooter>
    </Modal>
  );
}

function Labeled({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-pine">
        {label}
      </span>
      {children}
    </label>
  );
}

/* ─── Utilities ─────────────────────────────────────────────────── */

function splitName(fullName: string): {
  displayName: string;
  credentials: string;
} {
  const idx = fullName.indexOf(',');
  if (idx === -1) return { displayName: fullName.trim(), credentials: '' };
  return {
    displayName: fullName.slice(0, idx).trim(),
    credentials: fullName.slice(idx + 1).trim(),
  };
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/* ─── Side drawer ──────────────────────────────────────────────────
 * Opens when an admin clicks a contact card. Shows recent meetings
 * owned by this person (matched via email → users.id on the server)
 * and a 30-day finalized-activity sparkline. Deep-link at the
 * bottom goes to /admin/meetings pre-filtered by owner. */

interface ActivityMeeting {
  id: string;
  title: string;
  meetingDate: string;
  status: 'draft' | 'finalized' | 'soft_deleted';
  projectName: string | null;
  updatedAt: string;
}

interface ActivityResponse {
  resolvedUserId: string | null;
  recentMeetings: ActivityMeeting[];
  dailyFinalized: Array<{ date: string; count: number }>;
}

function TeamMemberDrawer({
  member,
  onClose,
}: {
  member: TeamMember;
  onClose: () => void;
}) {
  const { displayName, credentials } = splitName(member.fullName);
  const initials = initialsFor(displayName);
  const activityQ = useQuery({
    queryKey: ['ojb-team', member.id, 'activity'],
    queryFn: () =>
      api.get<ActivityResponse>(`/ojb-team/${member.id}/activity`),
  });
  const padded = usePaddedSparkline(activityQ.data?.dailyFinalized ?? []);
  const peak = padded.reduce(
    (acc, d) => (d.count > acc ? d.count : acc),
    0,
  );
  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onMouseDown={onClose}
    >
      <aside
        className="h-full w-full max-w-md overflow-y-auto border-l border-charcoal/15 bg-cloud/95 shadow-2xl backdrop-blur"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header: avatar + name + quick meta */}
        <div className="sticky top-0 z-10 border-b border-charcoal/10 bg-cloud/90 px-5 py-4 backdrop-blur">
          <div className="flex items-start gap-3">
            {member.headshotUrl ? (
              <img
                src={member.headshotUrl}
                alt={displayName}
                className="h-12 w-12 shrink-0 rounded-full object-cover ring-1 ring-pine/20"
              />
            ) : (
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-pine/15 text-sm font-semibold text-pine ring-1 ring-pine/25">
                {initials}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <h2 className="min-w-0 truncate text-base font-semibold text-charcoal">
                  {displayName}
                </h2>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="shrink-0 rounded-full p-1 text-charcoal/50 transition-colors hover:bg-charcoal/10 hover:text-charcoal"
                >
                  <X size={18} />
                </button>
              </div>
              {credentials && (
                <div className="mt-0.5 truncate text-[11px] font-medium uppercase tracking-[0.08em] text-pine/70">
                  {credentials}
                </div>
              )}
              {member.title && (
                <div className="mt-0.5 truncate text-[12px] text-charcoal/70">
                  {member.title}
                  {effectiveOffice(member) && (
                    <>
                      <span className="mx-1 text-charcoal/30">·</span>
                      {effectiveOffice(member)}
                    </>
                  )}
                </div>
              )}
              {member.email && (
                <a
                  href={`mailto:${member.email}`}
                  className="mt-0.5 block truncate text-[12px] text-pine/90 hover:text-pine hover:underline"
                >
                  {member.email}
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-5 px-5 py-5 text-sm">
          {/* Stats grid */}
          <div className="grid grid-cols-3 gap-3">
            <StatCell
              label="This month"
              value={member.stats?.monthToDate ?? 0}
              accent="pine"
            />
            <StatCell
              label="Finalized"
              value={member.stats?.finalizedAllTime ?? 0}
              accent="pine"
            />
            <StatCell
              label="30d peak"
              value={peak}
              accent={peak > 0 ? 'pine' : 'default'}
            />
          </div>

          {/* 30-day activity sparkline */}
          <div>
            <div className="mb-1 flex items-baseline justify-between text-[11px]">
              <span className="font-semibold uppercase tracking-[0.12em] text-charcoal/55">
                30-day activity
              </span>
              <span className="text-charcoal/45">
                Finalized meetings · America/New_York
              </span>
            </div>
            <div className="flex gap-0.5" style={{ height: '48px' }}>
              {padded.map((d) => {
                const pct =
                  peak === 0 ? 2 : Math.max(4, (d.count / peak) * 100);
                return (
                  <div
                    key={d.date}
                    className="group relative flex flex-1 flex-col justify-end"
                    title={`${d.date} - ${d.count} finalized`}
                  >
                    <div
                      className={
                        'w-full rounded-t transition-colors ' +
                        (d.count === 0 ? 'bg-charcoal/10' : 'bg-pine/60')
                      }
                      style={{ height: `${pct}%` }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent meetings */}
          <div>
            <div className="mb-2 flex items-baseline justify-between">
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-pine">
                Recent meetings
              </h3>
              {activityQ.data?.resolvedUserId && (
                <Link
                  to={`/admin/meetings?ownerUserId=${activityQ.data.resolvedUserId}`}
                  className="text-[11px] text-charcoal/60 hover:text-pine hover:underline"
                >
                  View all →
                </Link>
              )}
            </div>
            {activityQ.isLoading && (
              <div className="py-4 text-center text-xs italic text-charcoal/50">
                Loading…
              </div>
            )}
            {activityQ.data && !activityQ.data.resolvedUserId && (
              <div className="rounded-xl border border-dashed border-charcoal/20 px-3 py-4 text-center text-[11px] italic text-charcoal/55">
                {member.email
                  ? "No app activity yet - this person hasn't signed in."
                  : 'No email on this roster entry, so we can\'t match meetings.'}
              </div>
            )}
            {activityQ.data &&
              activityQ.data.resolvedUserId &&
              activityQ.data.recentMeetings.length === 0 && (
                <div className="rounded-xl border border-dashed border-charcoal/20 px-3 py-4 text-center text-[11px] italic text-charcoal/55">
                  No meetings yet.
                </div>
              )}
            {activityQ.data && activityQ.data.recentMeetings.length > 0 && (
              <ul className="divide-y divide-charcoal/10">
                {activityQ.data.recentMeetings.map((m) => (
                  <li key={m.id} className="py-2">
                    <Link
                      to={`/meetings/${m.id}/edit`}
                      className="flex items-baseline justify-between gap-2"
                    >
                      <span
                        className={
                          'truncate font-medium ' +
                          (m.status === 'finalized'
                            ? 'text-charcoal hover:text-pine'
                            : 'text-charcoal/70 hover:text-pine')
                        }
                      >
                        {m.title || (
                          <span className="italic text-charcoal/45">
                            Untitled
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 text-[10px] tabular-nums text-charcoal/50">
                        {m.meetingDate}
                      </span>
                    </Link>
                    <div className="text-[10px] text-charcoal/45">
                      {m.projectName ?? '-'}
                      {m.status !== 'finalized' && (
                        <span className="ml-1.5 rounded-full bg-charcoal/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-charcoal/60">
                          {m.status}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}

function StatCell({
  label,
  value,
  accent = 'default',
}: {
  label: string;
  value: number;
  accent?: 'default' | 'pine';
}) {
  return (
    <div className="rounded-xl border border-charcoal/10 bg-paper-light px-2 py-2 text-center">
      <div
        className={
          'text-lg font-semibold ' +
          (accent === 'pine' ? 'text-pine' : 'text-charcoal')
        }
      >
        {value}
      </div>
      <div className="text-[9px] uppercase tracking-[0.12em] text-charcoal/55">
        {label}
      </div>
    </div>
  );
}

/** Pad a sparse daily series to 30 days ending today (EST). Same
 *  contract as the Overview sparkline padder but colocated here
 *  because importing from a route is fragile. */
function usePaddedSparkline(
  data: Array<{ date: string; count: number }>,
): Array<{ date: string; count: number }> {
  return useMemo(() => {
    const map = new Map(data.map((d) => [d.date, d.count]));
    const estFmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const now = new Date();
    const out: Array<{ date: string; count: number }> = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const iso = estFmt.format(d);
      out.push({ date: iso, count: map.get(iso) ?? 0 });
    }
    return out;
  }, [data]);
}
